import React, {useEffect, useState} from "react";
import {Alert, Button, Form, Modal} from "react-bootstrap";
import {useNavigate} from "react-router-dom";
import {CognitoUserAttribute} from "amazon-cognito-identity-js";
import userpool from "../userpool";
import axiosInstance from "../axiosInstance";
import config from "../Config/config";
import "../css/SignupPage.css";

const SignupPage = ({show, handleClose}) => {
    useNavigate();
    const [formData, setFormData] = useState({
        phoneNumber: "",
        password: "",
        confirmPassword: "",
    });

    const [number, setNumber] = useState("");
    const [phoneNumberErr, setPhoneNumberErr] = useState("");
    const [passwordErr, setPasswordErr] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [step, setStep] = useState(1);
    const [otp, setOtp] = useState("");
    const [loading, setLoading] = useState(false);
    const [resendOtpTimer, setResendOtpTimer] = useState(60);

    useEffect(() => {
        if (step === 2 && resendOtpTimer > 0) {
            const timer = setTimeout(() => {
                setResendOtpTimer((prev) => prev - 1);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [resendOtpTimer, step]);

    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormData({...formData, [name]: value});
    };

    const validation = () => {
        return new Promise((resolve, reject) => {
            if (!formData.phoneNumber) {
                setPhoneNumberErr("Phone Number is required");
                reject("Phone Number is required");
            } else if (!formData.password) {
                setPasswordErr("Password is required");
                reject("Password is required");
            } else if (formData.password.length < 6) {
                setPasswordErr("Password must be at least 6 characters");
                reject("Password must be at least 6 characters");
            } else {
                resolve();
            }
        });
    };

    const sendOtp = (phone) => {
        setErrorMessage("");
        axiosInstance
            .get(`${config.api.otp}?number=${phone}`)
            .then(() => setResendOtpTimer(60))
            .catch((err) => console.error(err));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setPhoneNumberErr("");
        setPasswordErr("");
        setLoading(true);

        if (formData.password !== formData.confirmPassword) {
            setPasswordErr("Passwords do not match!");
            setLoading(false);
            return;
        }

        let formattedPhoneNumber = formData.phoneNumber.trim();
        if (!formattedPhoneNumber.startsWith("+91")) {
            formattedPhoneNumber = `+91${formattedPhoneNumber}`;
        }

        setNumber(formattedPhoneNumber);

        validation()
            .then(() => {
                const attributeList = [
                    new CognitoUserAttribute({
                        Name: "phone_number",
                        Value: formattedPhoneNumber,
                    }),
                ];

                userpool.signUp(
                    formattedPhoneNumber,
                    formData.password,
                    attributeList,
                    null,
                    (err) => {
                        if (err) {
                            setLoading(false);
                            if (err.message === "User already exists") {
                                setErrorMessage(
                                    "The user already exists but is not verified. If this account belongs to you, please verify it to proceed."
                                );
                            } else {
                                setErrorMessage(err.message);
                            }
                        } else {
                            setErrorMessage("");
                            setStep(2);
                            sendOtp(formattedPhoneNumber);
                        }
                        setLoading(false);
                    }
                );
            })
            .catch(() => setLoading(false));
    };

    const handleOtpSubmit = () => {
        if (!otp) {
            setErrorMessage("Please enter the OTP");
            return;
        }

        axiosInstance
            .post(`${config.api.otp}`, {phone_number: number, otp})
            .then((res) => {
                if (res.status === 202) {
                    handleClose();
                }
            })
            .catch((err) => {
                setErrorMessage(err.response?.data || "An unexpected error occurred");
            });
    };

    return (
        <div className="SignupPage">
            <Modal show={show} onHide={handleClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{step === 1 ? "Sign Up" : "Verify OTP"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {errorMessage && (
                        <Alert variant="danger" className="text-center">
                            {errorMessage}
                        </Alert>
                    )}

                    {step === 1 && (
                        <Form onSubmit={handleSubmit}>
                            <Form.Group controlId="phoneNumber">
                                <Form.Label>Phone Number</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter phone number"
                                    name="phoneNumber"
                                    value={formData.phoneNumber}
                                    onChange={handleChange}
                                />
                                {phoneNumberErr && (
                                    <p className="text-danger">{phoneNumberErr}</p>
                                )}
                            </Form.Group>

                            <Form.Group controlId="password">
                                <Form.Label>Password</Form.Label>
                                <Form.Control
                                    type="password"
                                    placeholder="Enter password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                />
                                {passwordErr && <p className="text-danger">{passwordErr}</p>}
                            </Form.Group>

                            <Form.Group controlId="confirmPassword">
                                <Form.Label>Confirm Password</Form.Label>
                                <Form.Control
                                    type="password"
                                    placeholder="Confirm password"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                />
                            </Form.Group>

                            {errorMessage.includes("The user already ") ? (
                                <Button
                                    type="button"
                                    className="mt-3"
                                    onClick={() => {
                                        setStep(2);
                                        sendOtp(number);
                                    }}
                                >
                                    Verify
                                </Button>
                            ) : (
                                <Button type="submit" className="mt-3" disabled={loading}>
                                    {loading ? "Loading..." : "Sign Up"}
                                </Button>
                            )}
                        </Form>
                    )}

                    {step === 2 && (
                        <Form>
                            <p>OTP sent to {formData.phoneNumber}</p>
                            <Form.Group controlId="otp">
                                <Form.Label>OTP</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter OTP"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value)}
                                />
                            </Form.Group>

                            <Button
                                variant="primary"
                                className="mt-3"
                                onClick={handleOtpSubmit}
                                block
                            >
                                Verify OTP
                            </Button>

                            {resendOtpTimer === 0 ? (
                                <Button
                                    variant="link"
                                    className="mt-3"
                                    onClick={() => sendOtp(number)}
                                    block
                                >
                                    Resend OTP
                                </Button>
                            ) : (
                                <p className="text-muted mt-3">
                                    Resend OTP in {resendOtpTimer} seconds
                                </p>
                            )}
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default SignupPage;
